import { useEffect, useMemo, useState } from "react";

import { defaultSettingTypes, booleanValues } from "../../config/settingConfig";
import GenericDropdown from "../controls/GenericDropdown";

const SettingItem = ({
    _id,
    settingsGroupIdentifier,
    initialSetting = {},
    validator,
    update,
    renderActionButton,
    customSettingTypesObj = [],
    customSettingTypes = [],
    setCustomValuesInUse,
    setCustomTypesInUse,
    permissions
}) => {
    const disabled = !permissions.update || initialSetting.parentLocked === true || ![null, undefined].includes(initialSetting.parentValue) || initialSetting.fixed === true;
    const valueDisabled = !permissions.update || initialSetting.parentLocked === true || initialSetting.fixed === true;

    const [currentName, setCurrentName] = useState(initialSetting.name);
    const [currentSequence, setCurrentSequence] = useState(initialSetting.sequence);
    const [currentType, setCurrentType] = useState(initialSetting.type);
    const [currentValue, setCurrentValue] = useState(initialSetting.value === null ? initialSetting.parentValue : initialSetting.value);
    const [currentDescription, setCurrentDescription] = useState(initialSetting.description);
    const [currentLocked, setCurrentLocked] = useState(initialSetting.locked);
    const [currentHidden, setCurrentHidden] = useState(initialSetting.hidden);

    const renderName = useMemo(() => {
        return (
            <input
                disabled={disabled}
                type="string"
                className={`form-control form-control-sm px-2 py-1 ${currentName?.trim().length === 0 ? 'border-danger' : ''}`}
                value={currentName}
                onInput={(event) => update({ fieldType: "Setting", newValue: event.target.value, initialValue: initialSetting.name, changedField: "name", _id, settingsGroupIdentifier, setterFunc: setCurrentName })} />
        );
    }, [_id, currentName, disabled, settingsGroupIdentifier, initialSetting.name, update]);

    const renderSequence = useMemo(() => {
        return (
            <input
                disabled={disabled}
                type="number"
                className="form-control form-control-sm px-2 py-1"
                value={currentSequence}
                onInput={(event) => update({ fieldType: "Setting", newValue: event.target.valueAsNumber, initialValue: initialSetting.sequence, changedField: "sequence", _id, settingsGroupIdentifier, setterFunc: setCurrentSequence })} />
        );
    }, [_id, currentSequence, disabled, initialSetting.sequence, settingsGroupIdentifier, update]);

    const renderType = useMemo(() => {
        return (
            <GenericDropdown
                options={[...defaultSettingTypes, ...customSettingTypesObj]}
                valueKey='_id'
                displayValueKey='name'
                readOnly={disabled}
                onSelect={(index) => update({ fieldType: "Setting", newValue: [...defaultSettingTypes, ...customSettingTypesObj].find(type => type._id === index).name, initialValue: initialSetting.type, changedField: "type", _id, settingsGroupIdentifier, setterFunc: (newVal) => { setCurrentType(newVal); setCurrentValue(""); } })}
                value={currentType}
                defaultValue={initialSetting.type}
                size="sm"
                grow={true}
            />
        );
    }, [_id, currentType, customSettingTypesObj, disabled, initialSetting.type, settingsGroupIdentifier, update]);

    const renderValue = useMemo(() => {
        if (currentType === "boolean") {
            return (
                <GenericDropdown
                    options={booleanValues}
                    valueKey='_id'
                    displayValueKey='name'
                    readOnly={valueDisabled}
                    onSelect={(index) => {
                        update({
                            _id,
                            initialValue: initialSetting.value,
                            settingsGroupIdentifier,
                            fieldType: "Setting",
                            newValue: booleanValues.find(val => val._id === index).name === "true",
                            changedField: "value",
                            parentValue: initialSetting.parentValue,
                            sequence: initialSetting.sequence,
                            name: initialSetting.name,
                            description: initialSetting.description,
                            type: initialSetting.type,
                            setterFunc: setCurrentValue,
                        });
                    }}
                    defaultValue={`${currentValue}`}
                    size="sm"
                    grow={true}
                />
            )
        }
        if (currentType === "number") {
            return (
                <input type="number"
                    disabled={valueDisabled}
                    className={`form-control form-control-sm px-2 py-1`}
                    onInput={(event) => { update({ _id, initialValue: initialSetting.value, settingsGroupIdentifier, fieldType: "Setting", newValue: event.target.value, changedField: "value", parentValue: initialSetting.parentValue, sequence: initialSetting.sequence, name: initialSetting.name, description: initialSetting.description, type: initialSetting.type, setterFunc: setCurrentValue }) }}
                    value={currentValue} />
            )
        }
        if (currentType === "string" || currentType === "array") {
            return (
                <textarea type="string"
                    disabled={valueDisabled}
                    className={`form-control form-control-sm px-2 py-1`}
                    rows="1"
                    onInput={(event) => { update({ _id, initialValue: initialSetting.value, settingsGroupIdentifier, fieldType: "Setting", newValue: event.target.value, changedField: "value", parentValue: initialSetting.parentValue, sequence: initialSetting.sequence, name: initialSetting.name, description: initialSetting.description, type: initialSetting.type, setterFunc: setCurrentValue }) }}
                    value={currentValue} />
            )
        }
        if (customSettingTypes.includes(currentType)) {
            return (
                <GenericDropdown
                    options={customSettingTypesObj.find(obj => obj.name === currentType).values.sort((a, b) => a.sequence - b.sequence)}
                    valueKey='_id'
                    displayValueKey='value'
                    readOnly={valueDisabled}
                    onSelect={(index) => {
                        update({
                            _id,
                            initialValue: initialSetting.value,
                            settingsGroupIdentifier,
                            fieldType: "Setting",
                            newValue: customSettingTypesObj.find(obj => obj.name === currentType).values.find(value => value._id === index).value,
                            changedField: "value",
                            parentValue: initialSetting.parentValue,
                            sequence: initialSetting.sequence,
                            name: initialSetting.name,
                            description: initialSetting.description,
                            type: initialSetting.type,
                            setterFunc: setCurrentValue,
                        });
                    }}
                    defaultValue={currentValue}
                    size="sm"
                    grow={true}
                />
            )
        }
        else {
            return null;
        }
    }, [_id, currentType, currentValue, customSettingTypes, customSettingTypesObj, initialSetting.description, initialSetting.name, initialSetting.parentValue, initialSetting.sequence, initialSetting.type, initialSetting.value, settingsGroupIdentifier, update, valueDisabled]);

    const renderDescription = useMemo(() => {
        return (
            <textarea type="string" disabled={disabled}
                className="form-control form-control-sm px-2 py-1"
                rows="1"
                onInput={(event) => update({ _id, initialValue: initialSetting.description, fieldType: "Setting", newValue: event.target.value, changedField: "description", settingsGroupIdentifier, setterFunc: setCurrentDescription })}
                value={currentDescription} />
        );
    }, [_id, currentDescription, disabled, initialSetting.description, settingsGroupIdentifier, update]);

    const renderLocked = useMemo(() => {
        return (
            <div className="d-flex align-items-center">
                <label className="d-block">Locked</label>
                <input
                    className={`form-check-input ms-2`}
                    type="checkbox"
                    disabled={!!currentHidden}
                    id={_id}
                    checked={currentLocked}
                    onChange={() => update({ _id, initialValue: initialSetting.locked, settingsGroupIdentifier, fieldType: "Setting", newValue: !currentLocked, changedField: "locked", setterFunc: setCurrentLocked })}
                ></input>
            </div>
        );
    }, [_id, currentHidden, currentLocked, initialSetting.locked, settingsGroupIdentifier, update]);

    const renderHidden = useMemo(() => {
        return (
            <div className="d-flex align-items-center">
                <label className="d-block">Hidden</label>
                <input
                    className={`form-check-input ms-2`}
                    type="checkbox"
                    id={_id}
                    checked={currentHidden}
                    onChange={() => update({ _id, initialValue: initialSetting.hidden, settingsGroupIdentifier, fieldType: "Setting", newValue: !currentHidden, changedField: "hidden", setterFunc: setCurrentHidden })}
                ></input>
            </div>
        )
    }, [_id, currentHidden, initialSetting.hidden, settingsGroupIdentifier, update]);

    const getActionButton = useMemo(() => {
        if (initialSetting.fixed === true && (currentName === "domainSecret")) {
            return (renderActionButton({ actionType: 'copy', valueToCopy: currentValue }));
        }
        if (initialSetting.parentLocked === true || initialSetting.fixed === true || !permissions.update) {
            return null;
        }
        if (initialSetting.parentValue !== null && currentValue !== initialSetting.parentValue) {
            return (renderActionButton({ actionType: 'reset', fieldType: "Setting", _id, groupingId: settingsGroupIdentifier, parentValue: initialSetting.parentValue, setterFunc: setCurrentValue }));
        }
        if (initialSetting.parentLocked !== true && initialSetting.parentValue === null && initialSetting.required !== true) {
            return (renderActionButton({ actionType: 'delete', fieldType: "Setting", _id, groupingId: settingsGroupIdentifier }));
        }

        return null;
    }, [_id, currentName, currentValue, initialSetting.fixed, initialSetting.parentLocked, initialSetting.parentValue, initialSetting.required, permissions.update, renderActionButton, settingsGroupIdentifier]);

    useEffect(() => {
        setCustomTypesInUse((currentIds) => {
            const newIds = { ...currentIds };

            if (customSettingTypes.includes(currentType)) {
                newIds[_id] = {
                    type: customSettingTypesObj.find(type => type.name === currentType)._id
                };

                if (currentValue) {
                    setCustomValuesInUse((currentIds) => {
                        const newIds = { ...currentIds };

                        newIds[_id] = {
                            value: customSettingTypesObj.find(type => type.name === currentType).values.find(value => value.value === currentValue)._id
                        };

                        return newIds;
                    });
                }
                else {
                    delete newIds[_id];
                }
            }
            else {
                delete newIds[_id];
            }

            return newIds;
        });
    }, [_id, currentType, currentValue, customSettingTypes, customSettingTypesObj, setCustomTypesInUse, setCustomValuesInUse]);

    useEffect(() => {
        if (currentHidden === true) {
            setCurrentLocked(true)
        }
    }, [currentHidden]);

    useEffect(() => {
        (typeof currentName === "string") && validator({ _id, field: 'name', fieldValid: currentName.trim().length > 0 });
    }, [_id, currentName, currentValue, validator]);

    const renderSetting = useMemo(() => {
        return (
            <div className="row align-items-center mt-2 flex-grow-1 setting-row" key={initialSetting._id}>
                <div className="col-fixed-5">
                    {renderSequence}
                </div>
                <div className="col">
                    {renderName}
                </div>
                <div className="col-2 col-xl-1">
                    {renderType}
                </div>
                <div className="col">
                    {renderValue}
                </div>
                <div className="col">
                    {renderDescription}
                </div>
                <div className="col-fixed-5">
                    {
                        !disabled && renderLocked
                    }
                </div>
                <div className="col-fixed-5">
                    {
                        !disabled && renderHidden

                    }
                </div>
                <span className="col-fixed-5 d-flex justify-content-end">
                    {
                        getActionButton
                    }
                </span>
            </div>
        );
    }, [disabled, initialSetting._id, renderDescription, renderHidden, renderLocked, renderName, renderSequence, renderType, renderValue, getActionButton]);

    return (
        <>
            {renderSetting}
        </>
    );
};

export default SettingItem;